import { useDispatch, useSelector } from "react-redux";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { updateConfig } from "../../../store/slices/currency/currencySlice";
import {
  getCurrencyRateDetails,
  updateFormData as updateRateFormData,
} from "../../../store/slices/currency/currencyRateFormSlice";
import {
  useGetCurrencyListDataQuery,
  useUpdateTableFieldsDataMutation,
  useUpdateCurrencyStatusMutation,
} from "../../../store/queries/currency";
import { useEffect, useMemo } from "react";
import { currency } from "../../../store/queries/currency";
import { current } from "@reduxjs/toolkit";

const useCurrency = () => {
  const menuState = useSelector((state) => state.currency);
  const { showCreateModal } = useSelector((state) => state.currency);
  const { showEditModal } = useSelector((state) => state.global);
  const { showRateEditModal } = useSelector((state) => state.currencyRateForm);
  const dispatch = useDispatch();
  const [updateTableFields] = useUpdateTableFieldsDataMutation();
  const [updateCurrencyStatus] = useUpdateCurrencyStatusMutation();

  const {
    data: mainData = {},
    isFetching,
    isLoading,
    refetch,
  } = useGetCurrencyListDataQuery({
    sort_by: menuState.sortBy,
    sort_order: menuState.sortOrder,
    search: menuState.search,
    per_page: menuState.currentPageSize,
    page: menuState.currentPage,
    is_active: menuState.is_active,
  });

  //clearing all the slice states initially
  useEffect(() => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = 1;
        state.scrollPage = 1;
        state.currentFilter = null;
        state.currentPageSize = 10;
        state.currentBranchFilter = [];
        state.clearSelection = false;
        state.queryData = {};
        state.queryStatus = "idle";
        state.sortBy = "";
        state.sortOrder = "desc";
        state.search = "";
        state.showCreateModal = false;
        state.selectedId = "";
        state.selectedItemsDetails = "";
        state.is_edit = false;
        state.showViewModal = false;
      })
    );
    //eslint-disable-next-line
  }, []);

  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
        state.clearSelection = true;
      })
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };

  let actionOptions = [{ label: "Edit Columns", value: 0 }];

  const handleActionChange = (item) => {
    if (item?.value === 0) {
      dispatch(
        globalUpdateConfig((state) => {
          state.showEditModal = true;
        })
      );
    }
  };
  const closeModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = false;
        state.is_edit = false;
        // state.selectedItemsDetails = ""
      })
    );
  };

  const handleSort = (label) => {
    if (menuState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.sortOrder = menuState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handleCreateClick = () => {
    dispatch(
      updateConfig((state) => {
        state.selectedItemsDetails = "";
      })
    );

    dispatch(
      updateConfig((state) => {
        state.showCreateModal = true;
      })
    );
  };

  const closeEditModal = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = false;
      })
    );
    dispatch(
      updateConfig((state) => {
        state.selectedItemsDetails = "";
      })
    );
  };

  const hasUpdatePermission = useMemo(() => {
    let permission = mainData?.data?.permission?.filter((p) =>
      Object.keys(p).includes("can_update")
    );
    return permission?.[0]?.can_update ?? 0;
    // eslint-disable-next-line
  }, [mainData]);

  const hasCreatePermission = useMemo(() => {
    let permission = mainData?.data?.permission?.filter((p) =>
      Object.keys(p).includes("can_create")
    );

    return permission?.[0]?.can_create ?? 0;
  }, [mainData]);

  const hasStatusUpdatePermission = useMemo(() => {
    let permission = mainData?.data?.permission?.filter((p) =>
      Object.keys(p).includes("can_change_status")
    );
    return permission?.[0]?.can_change_status ?? 0;
  }, [mainData]);

  const handleEditAction = (data) => {
    const tempResponse = mainData?.data?.data?.data.find((item) => {
      return item?.currency_id === data?.[0];
    });
    dispatch(
      updateConfig((state) => {
        state.selectedItemsDetails = tempResponse;
        state.showCreateModal = true;
        state.is_edit = true;
        state.selectedId = data?.[0];
      })
    );
  };

  // Opens the separate "Currency Edit" rate modal (Buy/Sell) for the selected row
  // Maps a currency-list row into the `buy_details`/`sell_details` shape the
  // rate form expects, so the modal can be pre-filled from the list when the
  // rate API has no saved margins yet (returns an empty array). The list row
  // only carries base rates (currency_code.buy_rate/sell_rate), fixed margins
  // (buy_fixed_rate/sell_fixed_rate) and status; markup_type is left blank for
  // the user to choose.
  const buildRateFallbackFromRow = (row) => {
    if (!row) return "";
    const buyRate = row?.currency_code?.buy_rate ?? "";
    const sellRate = row?.currency_code?.sell_rate ?? "";
    const buyMarkup = row?.buy_fixed_rate ?? "";
    const sellMarkup = row?.sell_fixed_rate ?? "";
    // Default margin type is Paisa (id 3) for the no-saved-data case.
    // Pre-compute the forex rate the same way the form's calc handlers do for
    // Paisa, so the disabled "Forex Rate" fields are populated on first render
    // instead of staying blank until the user edits the markup. Buy is additive
    // (rate + markup); Sell is subtractive (rate - markup).
    const buyForex = (
      (Number(buyRate) || 0) + (Number(buyMarkup) || 0)
    ).toFixed(4);
    const sellForex = (
      (Number(sellRate) || 0) - (Number(sellMarkup) || 0)
    ).toFixed(4);
    const common = {
      id: undefined,
      markup_type: 3,
      status: row?.status ?? 0,
      lock: 0,
      is_all_branches: 1,
      branch_ids: [],
    };
    return {
      buy_details: [
        {
          Cash: {
            ...common,
            rate: buyRate,
            markup_value: buyMarkup,
            total_amount: buyForex,
          },
        },
        {
          "Travel Card": {
            ...common,
            rate: buyRate,
            markup_value: buyMarkup,
            total_amount: buyForex,
          },
        },
      ],
      sell_details: {
        Cash: {
          ...common,
          rate: sellRate,
          markup_value: sellMarkup,
          total_amount: sellForex,
        },
      },
    };
  };

  const handleCurrencyRateEditAction = (data) => {
    const id = data?.[0];
    // The selected row from the list, used to pre-fill the modal when the rate
    // API returns an empty array (no saved rate data yet for this currency).
    const row = mainData?.data?.data?.data?.find(
      (item) => item?.currency_id === id
    );
    const rowData = buildRateFallbackFromRow(row);
    dispatch(getCurrencyRateDetails({ id, rowData }));
    dispatch(
      updateRateFormData((state) => {
        state.selectedId = id;
        state.selectedItemsDetails = "";
        state.showRateEditModal = true;
      })
    );
  };

  const closeRateEditModal = () => {
    dispatch(
      updateRateFormData((state) => {
        state.showRateEditModal = false;
        state.selectedItemsDetails = "";
      })
    );
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      currency_code: (field, data) => (
        <p className="pro-mb-0">{data[field]?.["currency_code"]}</p>
      ),
      status: (field, data) => {
        const isChecked = data?.status;
        return (
          <div className="pro-toggle">
            <div className="pro-toggle-box">
              <input
                id="status"
                name="status"
                type="checkbox"
                checked={isChecked}
                onChange={() =>
                  hasStatusUpdatePermission
                    ? toggleStatusById(data?.status, data?.currency_id)
                    : undefined
                }
                disabled={!hasStatusUpdatePermission}
              />
              <span></span>
            </div>
          </div>
        );
      },
    };

    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const toggleStatusById = (status, id) => {
    let formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("currency_id", id);
    formData.append("status", status === 1 ? 0 : 1);

    updateCurrencyStatus(formData);
    dispatch(
      currency.util.updateQueryData(
        "getCurrencyListData",
        {
          sort_by: menuState.sortBy,
          sort_order: menuState.sortOrder,
          search: menuState.search,
          per_page: menuState.currentPageSize,
          page: menuState.currentPage,
          is_active: menuState.is_active,
        },
        (cacheEntry) => {
          let currentCache = { ...current(cacheEntry) };
          const newResult = currentCache.data.data?.data?.map((res) =>
            res.currency_id === id
              ? {
                  ...res,
                  status: status === 1 ? 0 : 1,
                }
              : res
          );
          currentCache = {
            ...currentCache,
            data: {
              ...currentCache.data,
              data: {
                ...currentCache.data.data,
                data: newResult,
              },
            },
          };
          return currentCache;
        }
      )
    );
  };

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
      })
    );
  };

  return {
    menuState,
    mainData,
    isLoading,
    isFetching,
    currentPage: menuState.currentPage,
    paginationOptions,
    hasUpdatePermission,
    hasCreatePermission,
    showCreateModal,
    showEditModal,
    showRateEditModal,
    actionOptions,
    handleCreateClick,
    handleEditAction,
    handleCurrencyRateEditAction,
    closeRateEditModal,
    handleSort,
    updateTableFields,
    closeEditModal,
    closeModal,
    refetch,
    getRow,
    handlePageSize,
    handlePagination,
    handleActionChange,
    handleSearch,
  };
};

export default useCurrency;
